import type {
  AgeBand,
  CapabilityLevel,
  Communication,
  CommunicationCategory,
  CommunicationInput,
  CommunicationOutcome,
  CommunicationParticipant,
  HilloNumber,
  LearnerGrade,
} from '@iculogbook/shared';

/**
 * The state behind the Communication form, and the pure functions that move
 * it around. No React and no Dexie, so the rules that matter can be tested
 * directly — the same arrangement as `procedureForm.ts` and `edtForm.ts`.
 *
 * Shaped like a procedure's form rather than EDT's: there is a patient, so
 * age band and capability level are here and required, on the same terms as
 * every other clinical entry.
 */

export type CommunicationFormValues = {
  /** Null until chosen. Required, so the form cannot save without it. */
  category: CommunicationCategory | null;
  occurredAt: Date;
  ageBand: AgeBand | null;
  capabilityLevel: CapabilityLevel | null;
  withWhom: readonly CommunicationParticipant[];
  /** Null is a perfectly good final answer — not every conversation reaches one. */
  outcome: CommunicationOutcome | null;
  teachingGrades: readonly LearnerGrade[];
  note: string;
  hillos: readonly HilloNumber[];
};

export function initialValues(occurredAt: Date): CommunicationFormValues {
  return {
    category: null,
    occurredAt,
    ageBand: null,
    capabilityLevel: null,
    withWhom: [],
    outcome: null,
    teachingGrades: [],
    note: '',
    hillos: [],
  };
}

/**
 * Loads a stored entry, or an unsaved copy of one, back into the form. Absent
 * optional fields become the form's empty values; `buildCommunicationInput`
 * turns them back into absent fields on the way out.
 */
export function communicationValuesFrom(
  record: Communication | CommunicationInput,
): CommunicationFormValues {
  return {
    category: record.category,
    occurredAt: record.occurredAt,
    ageBand: record.ageBand,
    capabilityLevel: record.capabilityLevel,
    withWhom: record.withWhom ?? [],
    outcome: record.outcome ?? null,
    teachingGrades: record.teachingGrades ?? [],
    note: record.note ?? '',
    hillos: record.hillos ?? [],
  };
}

export type MissingField = 'category' | 'ageBand' | 'capabilityLevel';

/** The three choices with no safe default. Everything else is optional. */
export function missingRequired(values: CommunicationFormValues): MissingField[] {
  const missing: MissingField[] = [];
  if (values.category === null) missing.push('category');
  if (values.ageBand === null) missing.push('ageBand');
  if (values.capabilityLevel === null) missing.push('capabilityLevel');
  return missing;
}

/**
 * Turns the form state into the record the repository writes, or null if a
 * required choice is still missing.
 *
 * The result is still handed to `communicationInputSchema` before anything is
 * written. This function decides what to include; the shared schema decides
 * whether it is valid, and it is the only thing that does.
 */
export function buildCommunicationInput(values: CommunicationFormValues): CommunicationInput | null {
  if (values.category === null || values.ageBand === null || values.capabilityLevel === null) {
    return null;
  }

  const note = values.note.trim();

  return {
    category: values.category,
    occurredAt: values.occurredAt,
    ageBand: values.ageBand,
    capabilityLevel: values.capabilityLevel,
    ...(values.withWhom.length === 0 ? {} : { withWhom: [...values.withWhom] }),
    ...(values.outcome === null ? {} : { outcome: values.outcome }),
    ...(values.teachingGrades.length === 0 ? {} : { teachingGrades: [...values.teachingGrades] }),
    ...(note === '' ? {} : { note }),
    ...(values.hillos.length === 0 ? {} : { hillos: [...values.hillos] }),
  };
}
